import React from 'react';
import { Checkbox, Label, Separator } from '@roger/ui-components';
import type { AgencyClinicResponse } from './types';

/**
 * Edit which clinics a member can access — the set the backend clinic filter
 * enforces (EMR-783), distinct from clinic *roles*, which additionally make
 * the member assignable there.
 *
 * A clinic whose role is selected in this dialog is force-checked, because
 * assigning a clinic-scoped role grants access on save. Unchecking it gives up
 * those roles as well, rather than being silently undone: a role at a clinic
 * the member cannot see cannot be acted on, and this is the one place an
 * operator can undo an over-broad grant without hunting for the role that
 * caused it. The role checkboxes above clear at the same time, so the cascade
 * is visible before saving.
 */
export const ClinicAccessSection: React.FC<{
  clinics: AgencyClinicResponse[];
  /** Clinics currently checked for access. */
  accessClinicIds: ReadonlySet<string>;
  /** Clinics with a clinic-scoped role selected in this dialog. */
  roleSelectedClinicIds: ReadonlySet<string>;
  onToggle: (clinicId: string) => void;
  disabled?: boolean;
}> = React.memo(function ClinicAccessSection({
  clinics,
  accessClinicIds,
  roleSelectedClinicIds,
  onToggle,
  disabled,
}) {
  return (
    <div className='space-y-2'>
      <Separator />
      <div>
        <Label className='text-base font-semibold'>Clinic access</Label>
        <p className='text-xs text-muted-foreground'>
          Which clinics this member can see. Revoking keeps their history at the
          clinic but removes it from what they can view.
        </p>
      </div>
      <div className='space-y-2'>
        {clinics.map(clinic => {
          const grantedByRole = roleSelectedClinicIds.has(clinic.id);
          const checked = grantedByRole || accessClinicIds.has(clinic.id);
          return (
            <div key={clinic.id} className='flex items-start gap-2'>
              <Checkbox
                id={`clinic-access-${clinic.id}`}
                checked={checked}
                onCheckedChange={() => onToggle(clinic.id)}
                disabled={disabled}
              />
              <div className='flex flex-col'>
                <Label
                  htmlFor={`clinic-access-${clinic.id}`}
                  className='cursor-pointer'
                >
                  {clinic.name}
                </Label>
                {grantedByRole && (
                  <span className='text-xs text-muted-foreground'>
                    Granted by a clinic role above. Unchecking also removes
                    their roles at this clinic.
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
